import { useMoneyFormatter } from "@/lib/money-format";
import { useEffect, useRef, useState } from "react";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { ArrowLeft, Copy, Loader2, Pencil, RefreshCw, Send, Trash2, Undo2, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { DianReadinessBanner } from "@/components/companies/DianReadinessBanner";
import { ErrorState } from "@/components/ErrorState";
import { AppShell } from "@/components/layout/AppShell";
import { PosDocumentArtifactsPanel } from "@/components/pos-documents/PosDocumentArtifactsPanel";
import { PosDocumentForm } from "@/components/pos-documents/PosDocumentForm";
import { translatePosError } from "@/components/pos-documents/error-translations";
import { POS_STATUS_BADGE_VARIANT } from "@/components/pos-documents/format";
import { PosDocumentStatusTimeline } from "@/components/pos-documents/PosDocumentStatusTimeline";
import { RequireAuth } from "@/components/RequireAuth";
import { DiscardDraftDialog } from "@/components/shared/DiscardDraftDialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  useDiscardPosDocument,
  useEmitPosDocument,
  usePosDocument,
  useRefreshPosDocumentStatus,
} from "@/lib/api/pos-documents.queries";
import { usePosAdjustments } from "@/lib/api/pos-document-adjustments.queries";
import { useResolutions } from "@/lib/api/resolutions.queries";
import { useActiveCompany } from "@/lib/company-context";
import { useTeamPermission } from "@/lib/team-context";
import { showEmitErrorToast } from "@/lib/dian/emit-error-toast";
import {
  useCompanyDianReadiness,
  useDianReadinessReason,
} from "@/lib/dian/use-company-dian-readiness";

export const Route = createFileRoute("/companies/$companyId/pos/$documentId")({
  component: PosDocumentDetailPage,
});

function PosDocumentDetailPage() {
  return (
    <RequireAuth>
      <AppShell>
        <PosDocumentDetailContent />
      </AppShell>
    </RequireAuth>
  );
}

function PosDocumentDetailContent() {
  const formatPosMoney = useMoneyFormatter();
  const { t } = useTranslation();
  const { companyId, documentId } = Route.useParams();
  const navigate = useNavigate();
  const { setActiveCompanyId } = useActiveCompany();
  const query = usePosDocument(companyId, documentId);
  const resolutions = useResolutions(companyId, {
    documentKind: "pos_electronico",
    status: "all",
  });
  const emit = useEmitPosDocument(companyId);
  const refresh = useRefreshPosDocumentStatus(companyId);
  const discard = useDiscardPosDocument(companyId);
  const canWrite = useTeamPermission("pos.write");
  const relatedAdjustments = usePosAdjustments(
    query.data?.id ? companyId : undefined,
    { originalPosDocumentId: query.data?.id, pageSize: 50 },
  );
  const readiness = useCompanyDianReadiness(
    companyId,
    "pos_electronico",
    query.data?.resolutionId,
  );
  const readinessReason = useDianReadinessReason(readiness, t);
  const [editing, setEditing] = useState(false);
  const [discardOpen, setDiscardOpen] = useState(false);
  const actionInFlightRef = useRef(false);

  useEffect(() => {
    setActiveCompanyId(companyId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  if (query.isPending) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }
  if (query.isError || !query.data) {
    return (
      <ErrorState
        title={t("pos.detail.error_title")}
        description={t("pos.detail.error_body")}
        error={query.error}
        onRetry={() => void query.refetch()}
        retryLabel={t("common.retry")}
      />
    );
  }

  const document = query.data;
  const selectedResolution = (resolutions.data?.resolutions ?? []).find(
    (resolution) => resolution.id === document.resolutionId,
  );
  const resolutionEnvironment =
    document.resolutionEnvironment ?? selectedResolution?.environment;
  const canEmit =
    !!document.resolutionId &&
    (document.status === "draft" || document.status === "failed");
  // A draft, or a POS DIAN confirmed as rejected, can be reopened and corrected (PF-463).
  const canEdit = canWrite && document.editable;
  const canDiscard = canWrite && document.status === "draft" && document.documentNumber === 0;
  const artifactsEnabled = !!document.processId;

  async function onEmit() {
    if (actionInFlightRef.current) return;
    actionInFlightRef.current = true;
    try {
      await emit.mutateAsync({ documentId });
      toast.success(t("pos.detail.emit_queued"));
    } catch (error) {
      showEmitErrorToast(error, translatePosError(error, t), t, () =>
        void navigate({
          to: "/companies/$companyId/resoluciones",
          params: { companyId },
        }),
      );
    } finally {
      actionInFlightRef.current = false;
    }
  }

  async function onDiscard() {
    if (actionInFlightRef.current) return;
    actionInFlightRef.current = true;
    try {
      await discard.mutateAsync({ documentId, expectedRevision: document.revision });
      toast.success(t("pos.detail.discarded"));
      setDiscardOpen(false);
      void navigate({ to: "/companies/$companyId/pos", params: { companyId } });
    } catch (error) {
      toast.error(translatePosError(error, t));
      setDiscardOpen(false);
    } finally {
      actionInFlightRef.current = false;
    }
  }

  async function onRefresh() {
    if (actionInFlightRef.current) return;
    actionInFlightRef.current = true;
    try {
      await refresh.mutateAsync({ documentId });
      toast.success(t("pos.detail.refresh_done"));
    } catch (error) {
      toast.error(translatePosError(error, t));
    } finally {
      actionInFlightRef.current = false;
    }
  }

  async function copyCude() {
    if (!document.cude) return;
    await navigator.clipboard.writeText(document.cude);
    toast.success(t("pos.detail.cude_copied"));
  }

  return (
    <div className="mx-auto flex max-w-6xl flex-col gap-6">
      <DianReadinessBanner
        companyId={companyId}
        documentKind="pos_electronico"
        resolutionId={document.resolutionId}
      />
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <Link
            to="/companies/$companyId/pos"
            params={{ companyId }}
            className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-3 w-3" />
            {t("pos.detail.back")}
          </Link>
          <div className="mt-1 flex items-center gap-2">
            <h1 className="font-mono text-2xl font-semibold">{document.documentId}</h1>
            <Badge
              variant={POS_STATUS_BADGE_VARIANT[document.status]}
              className="text-[10px] uppercase"
            >
              {t(`pos.status.${document.status}`)}
            </Badge>
            {resolutionEnvironment ? (
              <Badge
                variant={
                  resolutionEnvironment === "hab" ? "warning" : "success"
                }
                className="text-[10px] uppercase"
              >
                {t(`resolutions.env.${resolutionEnvironment}`)}
              </Badge>
            ) : null}
          </div>
          <p className="mt-1 text-sm text-muted-foreground">
            {document.customerLegalName ?? t("pos.consumer_final")} · {formatPosMoney(document.payableAmount)}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {canEdit && !editing ? (
            <Button variant="outline" onClick={() => setEditing(true)}>
              <Pencil className="h-4 w-4" />
              {t("pos.detail.edit")}
            </Button>
          ) : null}
          {canDiscard && !editing ? (
            <Button variant="outline" onClick={() => setDiscardOpen(true)} disabled={discard.isPending}>
              <Trash2 className="h-4 w-4" />
              {t("pos.detail.discard")}
            </Button>
          ) : null}
          {document.processId ? (
            <Button
              variant="outline"
              disabled={refresh.isPending}
              onClick={() => void onRefresh()}
            >
              <RefreshCw className={refresh.isPending ? "h-4 w-4 animate-spin" : "h-4 w-4"} />
              {t("pos.detail.refresh_status")}
            </Button>
          ) : null}
          {canEmit && !editing ? (
            <Button
              disabled={emit.isPending || refresh.isPending || !readiness.ready}
              title={!readiness.ready ? readinessReason : undefined}
              onClick={() => void onEmit()}
            >
              <Send className="h-4 w-4" />
              {t("pos.detail.emit_now")}
            </Button>
          ) : null}
          {document.status === "emitted" ? (
            <>
              <Button
                variant="outline"
                onClick={() => void navigate({
                  to: "/companies/$companyId/pos/ajustes/new",
                  params: { companyId },
                  search: { fromPos: document.id, type: "credit", code: "2" },
                })}
              >
                <X className="h-4 w-4" />
                {t("pos.ajuste.cta.void")}
              </Button>
              <Button
                variant="outline"
                onClick={() => void navigate({
                  to: "/companies/$companyId/pos/ajustes/new",
                  params: { companyId },
                  search: { fromPos: document.id, type: "credit", code: "1" },
                })}
              >
                <Undo2 className="h-4 w-4" />
                {t("pos.ajuste.cta.return")}
              </Button>
              <Button
                variant="outline"
                onClick={() => void navigate({
                  to: "/companies/$companyId/pos/ajustes/new",
                  params: { companyId },
                  search: { fromPos: document.id },
                })}
              >
                {t("pos.ajuste.cta.adjust")}
              </Button>
            </>
          ) : null}
        </div>
      </div>

      {editing ? (
        <PosDocumentForm
          companyId={companyId}
          initialDocument={document}
          onSaved={() => setEditing(false)}
          onCancel={() => setEditing(false)}
        />
      ) : (

      <Tabs defaultValue="overview">
        <TabsList>
          <TabsTrigger value="overview">{t("pos.detail.tab.overview")}</TabsTrigger>
          <TabsTrigger value="lines">{t("pos.detail.tab.lines")}</TabsTrigger>
          <TabsTrigger value="dian">{t("pos.detail.tab.dian")}</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-4 grid gap-4 md:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">{t("pos.detail.sale.title")}</CardTitle>
              <CardDescription>{t("pos.detail.sale.subtitle")}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <Row label={t("pos.detail.field.customer")} value={document.customerLegalName ?? t("pos.consumer_final")} />
              <Row label={t("pos.detail.field.identification")} value={document.customerIdentificationNumber ?? "222222222222"} />
              <Row label={t("pos.detail.field.issue")} value={document.issueDateTime} />
              <Row label={t("pos.detail.field.payment_means")} value={t(`paymentMeans.${document.paymentMeansCode}`, { defaultValue: document.paymentMeansCode })} />
              <Row label={t("pos.detail.field.document_type")} value="20 · POS" />
              <Row
                label={t("pos.detail.field.environment")}
                value={
                  selectedResolution
                    ? t(`resolutions.env.${selectedResolution.environment}`)
                    : t("pos.detail.environment_unknown")
                }
              />
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle className="text-base">{t("pos.detail.station.title")}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <Row label={t("pos.form.terminal")} value={document.terminalId ?? "—"} />
              <Row label={t("pos.form.cash_register")} value={document.cashRegisterId ?? "—"} />
              <Row label={t("pos.form.cashier")} value={document.cashier ?? "—"} />
              <Row label={t("pos.form.sale_point")} value={document.salePoint ?? "—"} />
            </CardContent>
          </Card>
          <Card className="md:col-span-2">
            <CardHeader>
              <CardTitle className="text-base">{t("pos.totals.title")}</CardTitle>
            </CardHeader>
            <CardContent className="grid gap-x-8 gap-y-2 text-sm md:grid-cols-2">
              <Row label={t("pos.totals.subtotal")} value={formatPosMoney(document.lineExtensionAmount)} />
              <Row label={t("pos.totals.tax_exclusive")} value={formatPosMoney(document.taxExclusiveAmount)} />
              <Row label={t("pos.totals.iva")} value={formatPosMoney(document.ivaAmount)} />
              <Row label={t("pos.totals.inc")} value={formatPosMoney(document.incAmount)} />
              <Row label={t("pos.totals.discount")} value={formatPosMoney(document.allowanceTotalAmount)} />
              <Row label={t("pos.totals.charge")} value={formatPosMoney(document.chargeTotalAmount)} />
              <Row label={t("pos.totals.payable")} value={formatPosMoney(document.payableAmount)} bold />
            </CardContent>
          </Card>
          {(relatedAdjustments.data?.adjustments.length ?? 0) > 0 ? (
            <Card className="md:col-span-2">
              <CardHeader>
                <CardTitle className="text-base">{t("pos.ajuste.related.title")}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                {relatedAdjustments.data?.adjustments.map((row) => (
                  <Link
                    key={row.id}
                    to="/companies/$companyId/pos/ajustes/$adjustmentId"
                    params={{ companyId, adjustmentId: row.id }}
                    className="flex items-center justify-between rounded-md border px-3 py-2 hover:bg-accent"
                  >
                    <span>{row.documentId} · {t(`pos.ajuste.note_type.${row.noteType}`)}</span>
                    <span>{formatPosMoney(row.payableAmount)} · {t(`pos.status.${row.status}`)}</span>
                  </Link>
                ))}
              </CardContent>
            </Card>
          ) : null}
        </TabsContent>

        <TabsContent value="lines" className="mt-4">
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>#</TableHead>
                    <TableHead>{t("pos.form.line_description")}</TableHead>
                    <TableHead className="text-right">{t("pos.form.quantity")}</TableHead>
                    <TableHead className="text-right">{t("pos.form.unit_price")}</TableHead>
                    <TableHead className="text-right">{t("pos.form.tax")}</TableHead>
                    <TableHead className="text-right">{t("pos.totals.payable")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {document.lines.map((line) => {
                    const tax = line.ivaAmount + line.icAmount + line.icaAmount + line.incAmount;
                    return (
                      <TableRow key={line.id}>
                        <TableCell>{line.lineId}</TableCell>
                        <TableCell>
                          <p className="font-medium">{line.description}</p>
                          {line.sku ? <p className="font-mono text-xs text-muted-foreground">SKU {line.sku}</p> : null}
                          {line.standardCode ? (
                            <p className="font-mono text-xs text-muted-foreground">
                              {t("pos.form.standard_code")} {line.standardCode}
                            </p>
                          ) : null}
                        </TableCell>
                        <TableCell className="text-right font-mono">{line.quantity} {line.unitCode}</TableCell>
                        <TableCell className="text-right font-mono">{formatPosMoney(line.unitPrice)}</TableCell>
                        <TableCell className="text-right font-mono">{formatPosMoney(tax)}</TableCell>
                        <TableCell className="text-right font-mono">{formatPosMoney(line.lineExtensionAmount + tax)}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="dian" className="mt-4 grid gap-4 md:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">{t("pos.detail.timeline_title")}</CardTitle>
            </CardHeader>
            <CardContent>
              <PosDocumentStatusTimeline document={document} />
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle className="text-base">CUDE</CardTitle>
              <CardDescription>{t("pos.detail.cude_subtitle")}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {document.cude ? (
                <div className="rounded-md bg-muted p-3">
                  <p className="break-all font-mono text-xs">{document.cude}</p>
                  <Button className="mt-2" size="sm" variant="outline" onClick={() => void copyCude()}>
                    <Copy className="h-3.5 w-3.5" />
                    {t("pos.detail.copy_cude")}
                  </Button>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">{t("pos.detail.cude_pending")}</p>
              )}
              <Row label={t("pos.detail.field.dian_status")} value={document.dianStatus ?? "—"} />
              {document.dianStatusDescription ? (
                <p className="text-sm text-muted-foreground">{document.dianStatusDescription}</p>
              ) : null}
            </CardContent>
          </Card>
          <div className="md:col-span-2">
            <PosDocumentArtifactsPanel
              companyId={companyId}
              documentId={documentId}
              enabled={artifactsEnabled}
            />
          </div>
        </TabsContent>
      </Tabs>
      )}

      <DiscardDraftDialog
        open={discardOpen}
        onOpenChange={setDiscardOpen}
        title={t("pos.detail.discard_title", { documentId: document.documentId })}
        description={t("pos.detail.discard_body")}
        pending={discard.isPending}
        onConfirm={() => void onDiscard()}
      />
    </div>
  );
}

function Row({ label, value, bold }: { label: string; value: string; bold?: boolean }) {
  return (
    <div className={`flex items-center justify-between gap-3 ${bold ? "font-semibold" : ""}`}>
      <span className="text-muted-foreground">{label}</span>
      <span className="text-right font-mono">{value}</span>
    </div>
  );
}
